import React, {
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Dialog } from "@/components/main/utils/Dialog";
import { Tooltip } from "components/common/Tooltip";
import { useElementRect } from "components/hooks/useElementRect";
import { KeyActions } from "./KeyActions";
import { nameLength, type NamingMessage, type NamingPhase } from "./naming";

export interface NamingOverlayHandle {
  leave: () => Promise<void>;
  /// Shrinks the card into the row it made and lets `settle` replay the row's
  /// settle-in; with no row to land in it fades, and answers false.
  landIn: (
    findRow: () => HTMLElement | null,
    settle: (row: HTMLElement) => void,
  ) => Promise<boolean>;
}

const prefersReducedMotion = (): boolean =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

// jsdom has no Web Animations; without it the overlay simply cuts.
const canAnimate = <T extends Element>(el: T | null): el is T =>
  !!el && typeof el.animate === "function";

/// Motion tokens as WAAPI wants them; the CSS custom property is the source.
const motionToken = (name: string, fallback: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() ||
  fallback;
const durationMs = (name: string, fallback: number): number => {
  const value = motionToken(name, `${fallback}ms`);
  const ms = parseFloat(value);
  return value.endsWith("ms") ? ms : value.endsWith("s") ? ms * 1000 : fallback;
};

const nextFrame = () =>
  new Promise<void>((resolve) =>
    typeof requestAnimationFrame === "function"
      ? requestAnimationFrame(() => resolve())
      : setTimeout(resolve, 0),
  );

const ENTER_FROM = "translateY(4px) scale(0.985)";

interface NamingOverlayProps {
  ref?: React.Ref<NamingOverlayHandle>;
  open: boolean;
  ariaLabel: string;
  /// The card sits on this element's centre.
  anchorRef: React.RefObject<HTMLElement | null>;
  /// The pane that blurs; the rest of the app stays sharp but takes no clicks.
  paneRef: React.RefObject<HTMLElement | null>;
  inputRef: React.RefObject<HTMLInputElement | null>;
  /// Also the input's accessible name.
  placeholder: string;
  name: string;
  onNameChange: (name: string) => void;
  maxChars: number;
  message: NamingMessage | null;
  phase: NamingPhase;
  canCreate: boolean;
  glyph: React.ReactNode;
  glyphLabel: string;
  glyphRef: React.RefObject<HTMLButtonElement | null>;
  glyphExpanded: boolean;
  onGlyphClick: () => void;
  /// The footer's left side: where the new thing goes.
  footStart: React.ReactNode;
  onSubmit: () => void;
  onCancel: () => void;
}

/// The name card every create shares: a field centred on where the new row
/// lands, its actions floating beneath it on the pane's blur.
export const NamingOverlay: React.FC<NamingOverlayProps> = ({
  ref,
  open,
  ariaLabel,
  anchorRef,
  paneRef,
  inputRef,
  placeholder,
  name,
  onNameChange,
  maxChars,
  message,
  phase,
  canCreate,
  glyph,
  glyphLabel,
  glyphRef,
  glyphExpanded,
  onGlyphClick,
  footStart,
  onSubmit,
  onCancel,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const footRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const messageId = useId();
  const pane = useElementRect(paneRef, open);

  useLayoutEffect(() => {
    if (!open || prefersReducedMotion()) return;
    const duration = durationMs("--duration-short", 200);
    const easing = motionToken("--ease-enter", "ease-out");
    for (const el of [cardRef.current, footRef.current]) {
      if (!canAnimate(el)) continue;
      el.animate([{ opacity: 0, transform: ENTER_FROM }, { opacity: 1 }], {
        duration,
        easing,
      });
    }
  }, [open]);

  // The scrim mounts once the pane is measured, a frame after the card.
  const scrimShown = !!pane;
  useLayoutEffect(() => {
    const scrim = scrimRef.current;
    if (!scrimShown || !canAnimate(scrim) || prefersReducedMotion()) return;
    scrim.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: durationMs("--duration-short", 200),
      easing: motionToken("--ease-enter", "ease-out"),
    });
  }, [scrimShown]);

  const leave = async () => {
    if (prefersReducedMotion()) return;
    const duration = durationMs("--duration-micro", 100);
    const easing = motionToken("--ease-exit", "ease-in");
    const runs = [cardRef.current, footRef.current, scrimRef.current]
      .filter(canAnimate)
      .map(
        (el) =>
          el.animate(
            [
              { opacity: 1 },
              {
                opacity: 0,
                transform: el === scrimRef.current ? "none" : ENTER_FROM,
              },
            ],
            { duration, easing, fill: "forwards" },
          ).finished,
      );
    await Promise.allSettled(runs);
  };

  const landIn = async (
    findRow: () => HTMLElement | null,
    settle: (row: HTMLElement) => void,
  ) => {
    // The row mounts with the store update that answered the create.
    await nextFrame();
    await nextFrame();
    const row = findRow();
    const card = cardRef.current;
    if (!row) {
      await leave();
      return false;
    }
    if (!canAnimate(card) || prefersReducedMotion()) {
      settle(row);
      return true;
    }
    const from = card.getBoundingClientRect();
    const to = row.getBoundingClientRect();
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const duration = durationMs("--duration-medium", 350);
    const easing = motionToken("--ease-move", "ease-in-out");

    row.style.opacity = "0";
    if (canAnimate(footRef.current)) {
      footRef.current.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: durationMs("--duration-micro", 100),
        easing: motionToken("--ease-exit", "ease-in"),
        fill: "forwards",
      });
    }
    if (canAnimate(scrimRef.current)) {
      scrimRef.current.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration,
        easing,
        fill: "forwards",
      });
    }
    await card.animate(
      [
        { opacity: 1, transform: "none" },
        { opacity: 0.85, offset: 0.6 },
        {
          opacity: 0,
          transform: `translate(${dx}px, ${dy}px) scale(${to.width / from.width}, ${to.height / from.height})`,
        },
      ],
      { duration, easing, fill: "forwards" },
    ).finished;
    row.style.opacity = "";
    settle(row);
    return true;
  };

  useImperativeHandle(ref, () => ({ leave, landIn }));

  const length = nameLength(name);
  const over = length > maxChars;
  const busy = phase !== "idle";

  // ↵ creates from anywhere on the card. A button keeps its own Enter, except
  // a radio, whose Enter submits as a native one does.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    const target = e.target as HTMLElement;
    if (target.closest("button") && target.getAttribute("role") !== "radio") {
      return;
    }
    e.preventDefault();
    onSubmit();
  };

  return (
    <>
      {open &&
        pane &&
        createPortal(
          <div
            ref={scrimRef}
            aria-hidden
            className="naming-scrim z-40"
            style={{
              left: pane.left,
              top: pane.top,
              width: pane.width,
              height: pane.height,
            }}
          />,
          document.body,
        )}
      <Dialog
        open={open}
        bare
        anchorRef={anchorRef}
        initialFocusRef={inputRef}
        onClose={onCancel}
        ariaLabel={ariaLabel}
        backdropStyle={{ backgroundColor: "transparent" }}
        className="naming"
      >
        <div className="contents" onKeyDown={onKeyDown}>
          <div ref={cardRef} className="naming-card shadow-2xl">
            <div className="naming-field">
              <Tooltip title={glyphExpanded ? null : glyphLabel}>
                <span className="flex">
                  <button
                    ref={glyphRef}
                    type="button"
                    className="naming-glyph"
                    aria-label={glyphLabel}
                    aria-haspopup="dialog"
                    aria-expanded={glyphExpanded}
                    disabled={busy}
                    onClick={onGlyphClick}
                  >
                    {glyph}
                  </button>
                </span>
              </Tooltip>
              <input
                ref={inputRef}
                className="naming-input"
                type="text"
                value={name}
                placeholder={placeholder}
                aria-label={placeholder}
                aria-describedby={messageId}
                aria-invalid={over}
                autoComplete="off"
                spellCheck={false}
                readOnly={busy}
                onChange={(e) => onNameChange(e.target.value)}
              />
              {/* Near the cap only, so a normal name never shows it. */}
              {length >= maxChars - 20 && (
                <span className={`naming-count ${over ? "is-over" : ""}`}>
                  {length}/{maxChars}
                </span>
              )}
            </div>
            <div
              className={`naming-msg ${message ? "is-shown" : ""}`}
              data-kind={message?.kind}
              aria-live="polite"
            >
              <div>
                <div className="naming-msg-line">
                  <span className="naming-msg-icon" aria-hidden>
                    {message?.kind === "warn" ? (
                      <WarningAmberRoundedIcon sx={{ fontSize: 13 }} />
                    ) : message ? (
                      <ErrorOutlineRoundedIcon sx={{ fontSize: 13 }} />
                    ) : null}
                  </span>
                  <span id={messageId} className="naming-msg-text">
                    {message?.content}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div ref={footRef} className="naming-foot">
            {footStart}
            <span className="naming-gap" />
            <KeyActions
              className="naming-keys"
              confirmText={phase === "creating" ? "Creating" : "Create"}
              busy={phase === "creating"}
              disabled={busy || !canCreate || over}
              cancelDisabled={busy}
              onConfirm={onSubmit}
              onCancel={onCancel}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};
